import apiClient from './client';
import type {
  HealthResponse,
  PrepopRequest,
  PrepopResponse,
  BatchPrepopRequest,
  BatchPrepopResponse,
} from '@/types/api.types';

/**
 * Base URL path prefix shared by all APX Respond API endpoints.
 */
export const INSIGHT_BASE = '/v1/insight';

/**
 * Health check endpoint
 */
export async function getHealth(): Promise<HealthResponse> {
  const response = await apiClient.get<HealthResponse>(`${INSIGHT_BASE}/health`);
  return response.data;
}

/**
 * Single question prepopulation
 */
export async function prepopulate(request: PrepopRequest): Promise<PrepopResponse> {
  const response = await apiClient.post<PrepopResponse>(`${INSIGHT_BASE}/prepop`, request);
  return response.data;
}

/**
 * Batch prepopulation
 */
export async function batchPrepopulate(request: BatchPrepopRequest): Promise<BatchPrepopResponse> {
  const response = await apiClient.post<BatchPrepopResponse>(`${INSIGHT_BASE}/prepop/batch`, request);
  return response.data;
}

// -----------------------------------------------------------------------------
// Session Endpoints (P0-3: State Hydration)
// -----------------------------------------------------------------------------

import type {
  CreateSessionRequest,
  EstimateRequest,
  EstimateResponse,
  SessionProgress,
  SessionDetail,
  SessionListResponse,
  UpdateResponseActionRequest,
  CaptureNarrativeRequest,
  CaptureNarrativeResponse,
} from '@/types/api.types';

/**
 * Create a new DDQ session and start background processing
 */
export async function createSession(request: CreateSessionRequest): Promise<SessionProgress> {
  const response = await apiClient.post<SessionProgress>(`${INSIGHT_BASE}/sessions`, request);
  return response.data;
}

/**
 * List sessions for the current firm
 */
export async function listSessions(params?: {
  status?: string;
  limit?: number;
  offset?: number;
  firm_id?: number;
}): Promise<SessionListResponse> {
  const response = await apiClient.get<SessionListResponse>(`${INSIGHT_BASE}/sessions`, { params });
  return response.data;
}

/**
 * Get full session details with all responses
 */
export async function getSessionDetails(sessionId: string): Promise<SessionDetail> {
  const response = await apiClient.get<SessionDetail>(`${INSIGHT_BASE}/sessions/${sessionId}`);
  return response.data;
}

/**
 * Poll session processing status
 */
export async function getSessionStatus(sessionId: string): Promise<SessionProgress> {
  const response = await apiClient.get<SessionProgress>(`${INSIGHT_BASE}/sessions/${sessionId}/status`);
  return response.data;
}

/**
 * Update user action on a response (Accept/Modify/Reject)
 */
export async function updateResponseAction(
  sessionId: string,
  responseId: string,
  request: UpdateResponseActionRequest
): Promise<{ status: string; action: string }> {
  const response = await apiClient.patch<{ status: string; action: string }>(
    `${INSIGHT_BASE}/sessions/${sessionId}/responses/${responseId}`,
    request
  );
  return response.data;
}

/**
 * Soft delete a session
 */
export async function deleteSession(sessionId: string): Promise<{ status: string }> {
  const response = await apiClient.delete<{ status: string }>(`${INSIGHT_BASE}/sessions/${sessionId}`);
  return response.data;
}

/**
 * Export a session to XLSX or CSV format
 * Returns a blob for file download
 */
export async function exportSession(
  sessionId: string,
  format: 'xlsx' | 'csv',
  filter?: 'all' | 'accepted'
): Promise<Blob> {
  const params: Record<string, string> = { format };
  if (filter) {
    params.filter = filter;
  }
  const response = await apiClient.get(`${INSIGHT_BASE}/sessions/${sessionId}/export`, {
    params,
    responseType: 'blob',
  });
  return response.data;
}

// -----------------------------------------------------------------------------
// v2.0: Pre-Processing Estimate (P4-3)
// -----------------------------------------------------------------------------

/**
 * Get a pre-processing estimate for DDQ questions.
 * No session is created and no LLM tokens are consumed.
 */
export async function estimateSession(request: EstimateRequest): Promise<EstimateResponse> {
  const response = await apiClient.post<EstimateResponse>(
    `${INSIGHT_BASE}/sessions/estimate`,
    request
  );
  return response.data;
}

// -----------------------------------------------------------------------------
// Vault Narrative Capture (Two-Tier Architecture)
// -----------------------------------------------------------------------------

/**
 * Capture an approved DDQ answer to the vault for future reuse
 */
export async function captureNarrative(request: CaptureNarrativeRequest): Promise<CaptureNarrativeResponse> {
  const response = await apiClient.post<CaptureNarrativeResponse>(`${INSIGHT_BASE}/vault/narratives`, request);
  return response.data;
}

// -----------------------------------------------------------------------------
// Coverage Report (F3 accountability guarantee)
// -----------------------------------------------------------------------------

import type {
  CoverageReport,
  CreateGapRequestBody,
  GapRequestCreated,
  OnboardingReadiness,
  ServerExtractionResult,
} from '@/types/api.types';

/**
 * Get the per-session coverage report: every question answered-with-source
 * or explained-gap, with machine-readable gap reasons.
 */
export async function getSessionCoverage(sessionId: string): Promise<CoverageReport> {
  const response = await apiClient.get<CoverageReport>(
    `${INSIGHT_BASE}/sessions/${sessionId}/coverage`
  );
  return response.data;
}

// -----------------------------------------------------------------------------
// Server-Side Extraction (M2: F1/F5)
// -----------------------------------------------------------------------------

/**
 * Extract anchored questions from a .docx/.xlsx on the server. Stores the
 * original document for round-trip export and returns its blob ref.
 */
export async function extractQuestionsServer(file: File): Promise<ServerExtractionResult> {
  const form = new FormData();
  form.append('file', file);
  const response = await apiClient.post<ServerExtractionResult>(
    `${INSIGHT_BASE}/sessions/extract`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
}

// -----------------------------------------------------------------------------
// Self-Serve Onboarding Readiness (M3: F6)
// -----------------------------------------------------------------------------

/**
 * Instant readiness probe: what can Respond already answer for this firm?
 */
export async function getOnboardingReadiness(): Promise<OnboardingReadiness> {
  const response = await apiClient.get<OnboardingReadiness>(
    `${INSIGHT_BASE}/onboarding/readiness`
  );
  return response.data;
}

// -----------------------------------------------------------------------------
// Gap Routing (M4: F8)
// -----------------------------------------------------------------------------

/**
 * Create a tracked ask from a coverage gap; returns a ready mailto link.
 */
export async function createGapRequest(body: CreateGapRequestBody): Promise<GapRequestCreated> {
  const response = await apiClient.post<GapRequestCreated>(`${INSIGHT_BASE}/gap-requests`, body);
  return response.data;
}

// -----------------------------------------------------------------------------
// Document Workspace: Original-Document Export (M3)
// -----------------------------------------------------------------------------

/**
 * Export the original uploaded document prefilled with the session's answers.
 *
 * `filter=proposed` returns the original with ALL proposed answers + native
 * comments; `filter=accepted` returns approved-only. Returns the raw bytes as
 * an ArrayBuffer so the workspace editor can load the document in place.
 */
export async function exportOriginalDocument(
  sessionId: string,
  filter: 'proposed' | 'accepted' = 'proposed'
): Promise<ArrayBuffer> {
  const response = await apiClient.get<ArrayBuffer>(
    `${INSIGHT_BASE}/sessions/${sessionId}/export`,
    {
      params: { format: 'original', filter },
      responseType: 'arraybuffer',
    }
  );
  return response.data;
}
